import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { buildParamsFromObject } from 'src/app/common/utilities/api-helper';
import {
  S3DeleteFilesResponse,
  S3DeleteFolderResponse,
  S3DeleteResponse,
  S3FileInfoResponse,
  S3FileResponse,
  S3FolderResponse,
  S3MoveFilesResponse,
  S3MoveFolderResponse,
  S3Object,
  S3SavePayload,
  S3SaveResponse,
  S3UploadResponse,
} from '../../dtos/s3-file-manager.dto';

@Injectable({
  providedIn: 'root',
})
export class S3FileManagerService {
  private readonly apiUrl = EnvironmentService.apiUrl();

  private bucket = '';

  constructor(private apiService: APICoreService, private http: HttpClient) {}

  setBucket(bucket: string): void {
    this.bucket = bucket;
  }

  uploadFile(file: File, directory?: string): Observable<S3UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const url = directory
      ? `s3/${this.bucket}/upload?directory=${encodeURIComponent(directory)}`
      : `s3/${this.bucket}/upload`;
    return this.apiService.postFormRequest(url, formData);
  }

  uploadFileWithProgress(
    file: File,
    directory?: string
  ): Observable<HttpEvent<S3UploadResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    const url = directory
      ? `s3/${this.bucket}/upload?directory=${encodeURIComponent(directory)}`
      : `s3/${this.bucket}/upload`;
    return this.apiService.postFormRequest(url, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  saveFile(payload: S3SavePayload): Observable<S3SaveResponse> {
    return this.apiService.postJSONRequest(`s3/${this.bucket}/save`, payload);
  }

  downloadFile(key: string): Observable<S3FileResponse> {
    return this.apiService.getRequest(`s3/${this.bucket}/download-file`, {
      key,
    });
  }

  downloadFiles(keys: string[]): Observable<Blob> {
    const params = buildParamsFromObject({ keys });
    return this.http.get(
      `${this.apiUrl}/s3/${this.bucket}/download-files?${params}`,
      { responseType: 'blob' }
    );
  }

  getFileInfo(key: string): Observable<S3FileInfoResponse> {
    return this.apiService.getRequest(`s3/${this.bucket}/info`, { key });
  }

  deleteFile(key: string): Observable<S3DeleteResponse> {
    return this.apiService.deleteRequest(`s3/${this.bucket}/file`, { key });
  }

  listObjects(directory?: string): Observable<S3Object[]> {
    return this.apiService.getRequest(`s3/${this.bucket}/list`, {
      ...(directory ? { directory } : {}),
    });
  }

  createFolder(path: string): Observable<S3FolderResponse> {
    return this.apiService.postJSONRequest(`s3/${this.bucket}/folder`, {
      path,
    });
  }

  deleteFolder(path: string): Observable<S3DeleteFolderResponse> {
    return this.apiService.deleteRequest(`s3/${this.bucket}/folder`, { path });
  }

  deleteFiles(keys: string[]): Observable<S3DeleteFilesResponse> {
    return this.http.delete<S3DeleteFilesResponse>(
      `${this.apiUrl}/s3/${this.bucket}/files`,
      { body: { keys } }
    );
  }

  moveFiles(
    keys: string[],
    destination: string
  ): Observable<S3MoveFilesResponse> {
    return this.apiService.postJSONRequest(`s3/${this.bucket}/move-files`, {
      keys,
      destination,
    });
  }

  moveFolder(
    path: string,
    destination: string
  ): Observable<S3MoveFolderResponse> {
    return this.apiService.postJSONRequest(`s3/${this.bucket}/move-folder`, {
      path,
      destination,
    });
  }
}
